import { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Button,
  CardContent,
  CardHeader,
  Card,
} from "@material-ui/core";

import ClearButton from "components/_refactor/button/clear";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import DialogRegistroFalha from "components/Dialogs/DialogRegistroFalha";
import PeriodField, {
  getPeriodLabel,
} from "components/_refactor/fields/period-field";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import Api from "lib/api/httpRequestHelper";
import LoadingData from "components/Loading/LoadingData";
import TableRegistrarFalha from "./table/TableRegistrarFalha";
import moment from "moment";
import { IsSmall, IsMobile900, IsMobile } from "utils/is-mobile";

const situacaoOptions = [
  { value: "PENDENTES", name: "Pendente" },
  { value: "RESOLVIDAS", name: "Resolvida" },
  { value: "TODAS", name: "Todas" },
];

const useStyles = makeStyles(componentStyles);

function RegistrarTearFalha() {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [fails, setFails] = useState(null);
  const [fetchingReasons, setFetchingReasons] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [tableTitle, setTableTitle] = useState(null);
  const [searching, setSearching] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [situation, setSituation] = useState("TODAS");

  const [totalRegisters, setTotalRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);
  const [lastSearchParams, setLastSearchParams] = useState(null);

  const isMobile = IsSmall();
  const isMobile900 = IsMobile900();
  const isMobile700 = IsMobile();

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const fetchReasonsReturn = useCallback(async () => {
    setFetchingReasons(true);

    await Api.get("weave-fail-motive-list", "weave", "", true)
      .then((res) => {
        setReasons(res.Result);
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });      })
      .finally(() => setFetchingReasons(false));
  }, [snackbar]);

  const handleSearch = async ({ page = 1, sizePerPage = 25 }) => {
    const { periodo, motivo, dataInicial, dataFinal } = values;

    setSearching(true);
    setCancelLoading(false);
    setCancelLoading(false);
    setLastSearchParams(null);

    const initialFormat = moment(dataFinal).format("YYYY-MM-DD");
    const finalFormat = moment(dataFinal).format("YYYY-MM-DD");

    setLastSearchParams({
      idMotivo: motivo?.IdMotivoFalha || "",
      situacao: situation,
      dataInicial: initialFormat,
      dataFinal: finalFormat,
    });

    await Api.get(
      "weave-register-list",
      "weave",
      "?situacao=" +
        situation +
        "&idMotivo=" +
        (motivo?.IdMotivoFalha || "") +
        `&page=${page - 1}&pageSize=${sizePerPage}` +
        (periodo ? `&dtIni=${dataInicial}&dtFim=${dataFinal}` : ""),
      true
    )
      .then((res) => {
        if (values.periodo) {
          setTableTitle(
            `Registros de Falhas ${getPeriodLabel({
              period: values.periodo?.value,
              initialDate: values.dataInicial,
              finalDate: values.dataFinal,
            })}`
          );
        } else {
          setTableTitle(`Registros de Falhas`);
        }
        setCurrentPage(page);
        setCurrentSizePerPage(sizePerPage);
        setFails(res.Result.ListaLancamentoFalhas);
        setTotalRegisters(res.Result.TotalRegistro);
        setSearching(false);
      })
      .catch((error) => {
        setSearching(false);
        setErrorMessage(true);
        console.log(error);      })
      .finally(() => {
        setSearching(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      motivo: "",
      periodo: null,
      dataInicial: null,
      dataFinal: null,
    },
  });

  const { values, handleChange } = formik;

  const getDefaultReason = useCallback(
    (idMotivoFalha) => {
      return reasons?.find((reason) => reason.IdMotivoFalha === idMotivoFalha);
    },
    [reasons]
  );

  const handleClearFilters = () => {
    setClearFilters(true);
    setSearching(true);
    handleChange({
      target: {
        name: "periodo",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataInicial",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataFinal",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "motivo",
        value: "",
      },
    });
    setSituation("TODAS");
    setSearching(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    setTableTitle(null);
    setFails(null);
  };

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(
        "Ocorreu um erro ao buscar registros de falha. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
        {
          severity: "error",
        }
      );
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (values?.periodo === null && values?.motivo === "" && clearFilters) {
      setClearFilters(false);
    }
  }, [values, clearFilters]);

  useEffect(() => {
    fetchReasonsReturn();
  }, []);

  return (
    <>
      <HeaderClientes section="Registrar Falha" subsection="Tecelagem" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={isMobile700 ? 12 : isMobile900 ? 6 : 4}>
                    <AutoCompleteField
                      label="Motivo da Falha"
                      onChange={(value) => {
                        handleChange({
                          target: { name: "motivo", value },
                        });
                      }}
                      value={getDefaultReason(values?.motivo?.IdMotivoFalha)}
                      options={reasons}
                      disabled={searching || fetchingReasons}
                      optionLabelKey="DescMotivoFalha"
                      optionIdKey="IdMotivoFalha"
                      loading={searching || clearFilters}
                    />
                  </Grid>

                  <Grid item xs={isMobile700 ? 12 : isMobile900 ? 6 : 4}>
                    <AutoCompleteField
                      label="Situação da Falha"
                      required
                      options={situacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        situacaoOptions.find(
                          (item) => item.value === situation
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        setSituation(newValue ? newValue.value : "");
                      }}
                    />
                  </Grid>

                  <Grid item xs={values.periodo?.value === "Outros" ? "12" : 4}>
                    <PeriodField
                      label="Período"
                      value={values.periodo}
                      onChange={(option) => {
                        handleChange({
                          target: { name: "periodo", value: option },
                        });
                      }}
                      initialDate={values.dataInicial}
                      finalDate={values.dataFinal}
                      setInitialDate={(value) => {
                        handleChange({
                          target: { name: "dataInicial", value },
                        });
                      }}
                      setFinalDate={(value) => {
                        handleChange({
                          target: { name: "dataFinal", value },
                        });
                      }}
                      disabled={searching}
                    />
                  </Grid>
                  <Grid item xs={isMobile700 ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      color="default"
                      variant="contained"
                      onClick={() => {
                        handleSearch({
                          page: 1,
                          sizePerPage: 25,
                        });
                      }}
                      disabled={searching || fetchingReasons}
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile700 ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      fullWidth={true}
                      disabled={searching || fetchingReasons}
                      onClick={() => handleClearFilters()}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
          {Boolean(fails) && !searching && !cancelLoading && (
            <Grid item xs={12}>
              <TableRegistrarFalha
                dataTable={fails}
                totalRegisters={totalRegisters}
                currentPage={currentPage}
                currentSizePerPage={currentSizePerPage}
                handleSearch={handleSearch}
                setSelectedRecord={setSelectedRecord}
                toggleFormDialog={toggleFormDialog}
                searching={searching}
                tableTitle={tableTitle}
                lastSearchParams={lastSearchParams}
              />
            </Grid>
          )}
        </Grid>
      </Container>
      {openFormDialog ? (
        <DialogRegistroFalha
          open
          formType={openFormDialog}
          onClose={(updatedRecord) => {
            setSelectedRecord(null);
            setOpenFormDialog(null);

            if (updatedRecord?.IdLancamentoFalha) {
              handleSearch({
                page: 1,
                sizePerPage: 25,
              });
            }
          }}
          selectedRecord={selectedRecord}
        />
      ) : null}
    </>
  );
}

RegistrarTearFalha.propTypes = {};

export default RegistrarTearFalha;
